import './AddFeed.css';
import './AddNewsletter.css';
import { useState, useContext } from 'react';
import { FlowForm } from 't-lib-ui/structure';
import { TextButton, Input, Spinner } from 't-lib-ui/widgets';
import { RouterContext, goTo } from 't-lib-ui/router';
import { nonNull } from 't-lib-ui/helpers';
import { AppState } from '../context/general';
import { createNewsletter } from '../connector/api';
import { RestCreateNewsletterResult } from '../schema-copy/api-schema';
import { getPlanLimits, planDisplayNames as PLAN_LABEL, UserPlan } from '../schema-copy/userPlans';
import { useFeedQuota } from '../connector/feed-quota';
import MailIcon from './MailIcon';
import CheckmarkCircleIcon from './CheckmarkCircleIcon';
// Friendly cap shown to users (the stored history is a little higher).
const KEPT_EMAILS = 1000;

interface AddNewsletterProps {
  onCreated?: (feedId: string) => void;
}

const AddNewsletter: React.FC<AddNewsletterProps> = ({ onCreated }) => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);

  const countNewsletters = () => appState.feedEngine.getFeeds().filter(f => f.feedStatus == 'NEWSLETTER').length;

  const [name, setName] = useState(`Newsletter ${countNewsletters() + 1}`);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RestCreateNewsletterResult | null>(null);
  const [copied, setCopied] = useState(false);

  const plan = (appState.state.currentUser?.userPlan ?? 'free') as UserPlan;
  const maxInboxes = getPlanLimits(plan).maxNewsletterFeeds;
  const usedInboxes = countNewsletters();
  const remaining = Math.max(0, maxInboxes - usedInboxes);
  // The plan doesn't include newsletters at all (quota of 0) — distinct from having
  // a quota but having used it all up.
  const newslettersAllowed = maxInboxes > 0;
  const atLimit = remaining <= 0;
  // A newsletter also counts against the shared total-feed quota. Block (and message) proactively when
  // the total is full, so the user isn't offered a Create button that can only fail — and so the "out
  // of feeds" case is distinct from "out of newsletter inboxes".
  const { atLimit: atTotalFeedLimit, max: maxTotalFeeds } = useFeedQuota();
  const canCreate = !!name.trim() && !creating && newslettersAllowed && !atLimit && !atTotalFeedLimit;

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    try {
      const auth = nonNull(appState.state.authData);
      const res = await createNewsletter(auth, name.trim());
      // Register the new feed so it shows up in the sidebar immediately.
      appState.feedEngine.handleSubscribeResult(res);
      setResult(res);
      onCreated?.(res.feed.id);
    } catch (e) {
      // A newsletter is subject to BOTH quotas, so tell the user which one they actually hit:
      // 'feed' = the shared total-feed limit, 'newsletter-feed' = the dedicated newsletter-inbox limit.
      const objectName = e && typeof e == 'object' ? (e as any).objectName : undefined;
      if (e && typeof e == 'object' && 'type' in e && (e as any).type == 'Quota')
        setError(objectName == 'feed'
          ? `You've reached your plan's total feed limit of ${maxTotalFeeds}. Unsubscribe a feed to make room for a newsletter.`
          : "You've reached the newsletter-inbox limit on your plan.");
      else
        setError('Could not create the inbox: ' + ((e as Error).message || 'unknown error'));
    } finally {
      setCreating(false);
    }
  };

  const copyEmail = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context) — the address is still shown.
    }
  };

  const createAnother = () => {
    setResult(null);
    setError(null);
    setCopied(false);
    setName(`Newsletter ${countNewsletters() + 1}`);
  };

  const inboxWord = (n: number) => `inbox${n == 1 ? '' : 'es'}`;

  return (
    <div className="addFeed addNewsletter">
      <FlowForm main={true}>
        <div className="addFeedContainer">
          <div className="addFeedContent">
            {!newslettersAllowed ? (
              <div className="nlHero">
                <span className="nlHeroIcon"><MailIcon size={24} color="currentColor" /></span>
                <div className="nlHeroText">
                  <h2 className="nlHeroTitle">Newsletters aren't available on your plan</h2>
                  <p className="nlHeroLede">
                    Your current {PLAN_LABEL[plan]} plan doesn't include newsletter inboxes.
                    To read email newsletters as feeds, upgrade to a plan that includes them.
                  </p>
                  <div className="nlDoneActions" style={{ marginTop: '1.25rem' }}>
                    <TextButton label="Back to feeds" type="major" focussable="flow" onClick={() => goTo(routerCtx, '/a/add')} />
                  </div>
                </div>
              </div>
            ) : !result ? (
              <>
                <div className="nlHero">
                  <span className="nlHeroIcon"><MailIcon size={24} color="currentColor" /></span>
                  <div className="nlHeroText">
                    <h2 className="nlHeroTitle">Create a newsletter inbox</h2>
                    <p className="nlHeroLede">
                      Read email newsletters right here in Downread. We'll give you a private email
                      address for every newsletter. Every email arrives as a post in this new feed.
                    </p>
                  </div>
                </div>

                <div className="nlCard">
                  <label className="nlLabel" htmlFor="nlName">Name this inbox</label>
                  <Input
                    value={name}
                    maxLength={30}
                    placeholder="Newsletter name"
                    onInput={setName}
                    onEnter={create}
                    layout="singleCol"
                  />

                  {error && <div className="nlError">{error}</div>}

                  <div className="nlCardActions">
                    {creating
                      ? <span className="nlCreating"><Spinner spinning={true} /> Creating your inbox…</span>
                      : <TextButton label="Create inbox" type="major" focussable="flow" enabled={canCreate ? 'enabled' : 'disabled'} onClick={create} />
                    }
                  </div>

                  <div className={`nlQuota${atLimit ? ' nlQuotaFull' : ''}`}>
                    {atLimit
                      ? <>You've reached your plan limit of <strong>{maxInboxes}</strong> newsletter {inboxWord(maxInboxes)} on the {PLAN_LABEL[plan]} plan.</>
                      : <>Using <strong>{usedInboxes}</strong> of <strong>{maxInboxes}</strong> newsletter {inboxWord(maxInboxes)} on the {PLAN_LABEL[plan]} plan.</>
                    }
                  </div>

                  {/* Distinct from the inbox limit: the shared total-feed quota is full, so no new feed
                      of any kind (including a newsletter) can be added until one is removed. */}
                  {!atLimit && atTotalFeedLimit && (
                    <div className="nlQuota nlQuotaFull">
                      You've reached your plan's total feed limit of <strong>{maxTotalFeeds}</strong>. Unsubscribe a feed to make room for a newsletter.
                    </div>
                  )}
                </div>

                <div className="nlHow">
                  <h3 className="nlHowTitle">How it works</h3>
                  <ol className="nlHowSteps">
                    <li>
                      <span className="nlStepNum">1</span>
                      <div className="nlStepBody"><strong>Get your address</strong><span>Each inbox has its own private email address.</span></div>
                    </li>
                    <li>
                      <span className="nlStepNum">2</span>
                      <div className="nlStepBody"><strong>Subscribe or forward</strong><span>Use it to sign up for newsletters, or forward ones you already receive.</span></div>
                    </li>
                    <li>
                      <span className="nlStepNum">3</span>
                      <div className="nlStepBody"><strong>Read them as a feed</strong><span>Every email shows up as a post. Only you can see it.</span></div>
                    </li>
                  </ol>
                  <p className="nlNote">Each inbox keeps your most recent {KEPT_EMAILS.toLocaleString()} emails.</p>
                </div>
              </>
            ) : (
              <>
                <div className="nlHero">
                  <span className="nlHeroIcon nlHeroIconSuccess"><CheckmarkCircleIcon size={24} color="currentColor" /></span>
                  <div className="nlHeroText">
                    <h2 className="nlHeroTitle">Your inbox is ready</h2>
                    <p className="nlHeroLede">Send newsletters to this address and they'll appear in <strong>{result.feed.title}</strong>.</p>
                  </div>
                </div>

                <div className="nlCard nlEmailCard">
                  <div className="nlEmailRow">
                    <span className="nlEmail" title={result.email}>{result.email}</span>
                    <TextButton label={copied ? 'Copied!' : 'Copy'} type="major" size="small" focussable="flow" onClick={copyEmail} />
                  </div>
                </div>

                <div className="nlHow">
                  <h3 className="nlHowTitle">How to continue</h3>
                  <ol className="nlHowSteps">
                    <li>
                      <span className="nlStepNum">1</span>
                      <div className="nlStepBody"><strong>Use the address</strong><span>Subscribe to a newsletter with it, or set up forwarding from your email provider.</span></div>
                    </li>
                    <li>
                      <span className="nlStepNum">2</span>
                      <div className="nlStepBody"><strong>Wait for the first email</strong><span>New emails arrive automatically as posts — the first one can take a few minutes.</span></div>
                    </li>
                  </ol>
                  <p className="nlNote">Up to 20 emails per hour are stored, and your inbox keeps its most recent {KEPT_EMAILS.toLocaleString()} emails.</p>
                </div>

                <div className="nlDoneActions">
                  <TextButton label="View feed" type="major" focussable="flow" onClick={() => goTo(routerCtx, `/a/feed/${result.feed.id}`)} />
                  <TextButton label="Create another" type="minor" focussable="sideway" onClick={createAnother} />
                </div>
              </>
            )}
          </div>
        </div>
      </FlowForm>
    </div>
  );
};

export default AddNewsletter;
